import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Tooltip, // ✅ Added Tooltip import
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, Link, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
// import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DraftsIcon from "@mui/icons-material/Drafts";
import TransformIcon from "@mui/icons-material/Transform";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";

import logo from "../../../assets/images/logotis.jpg";
import accountImg from "../../../assets/images/accout.png";
import { logoutUser, getRole } from "../../../utils/auth";

const UserSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery("(max-width:768px)");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
    handleMenuClose();
    setDrawerOpen(false);
  };

  const role = getRole();

  const allNavItems = [
    {
      icon: HomeIcon,
      path: "/dashboard",
      label: "Dashboard",
      roles: ["admin", "user", "subadmin"],
    }, // or "user"
    {
      icon: ViewModuleIcon,
      path: "/wapp",
      label: "Wapp",
      roles: ["admin", "user", "subadmin"],
    }, // or "user"
    {
      icon: DashboardIcon,
      path: "/wbtn",
      label: "Wbtn",
      roles: ["admin", "user", "subadmin"],
    }, // or "user"
    {
      icon: DescriptionIcon,
      path: "/report",
      label: "Report",
      roles: ["admin", "user", "subadmin"],
    }, // or "user"
    {
      icon: TransformIcon,
      path: "/transaction",
      label: "Transaction",
      roles: ["admin", "user", "subadmin"],
    }, // or "user"
    {
      icon: NotificationsIcon,
      path: "/notification",
      label: "Notification",
      roles: ["admin"],
    },
    {
      icon: DraftsIcon,
      path: "/campaign",
      label: "Campaign",
      roles: ["admin"],
    },
    { icon: PersonIcon, path: "/users", label: "Users", roles: ["admin"] },
  ];

  // Filter nav items based on current user role
  const navItems = allNavItems.filter((item) => item.roles.includes(role));

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "white",
        borderRadius: "80px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",

        px: { xs: 2, sm: 3, md: 6 },
        overflow: "hidden",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",

          position: "relative",
        }}
      >
        {/* Left Logo */}
        <Box display="flex" alignItems="center" gap={2} zIndex={2}>
          <img src={logo} alt="Logo" style={{ height: 70 }} />
        </Box>

        {/* Center Navigation (desktop & tablet) */}
        {!isMobile && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: isTablet ? 1 : 1.5,
              zIndex: 1,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            {navItems.map(({ icon: Icon, path, label }, index) => {
              const isActive = location.pathname === path;
              const iconSize = isTablet ? 16 : 20;

              return isActive ? (
                <Button
                  key={index}
                  component={Link}
                  to={path}
                  startIcon={
                    <Icon sx={{ fontSize: iconSize, color: "#fff" }} />
                  }
                  sx={{
                    backgroundImage:
                      "linear-gradient(to right, #4f46e5, #3b82f6)",
                    color: "#fff",
                    borderRadius: "30px",
                    textTransform: "none",
                    px: 2,
                    fontSize: isTablet ? "0.7rem" : "0.9rem",

                    "&:hover": { backgroundColor: "#3b82f6" },
                  }}
                >
                  {label}
                </Button>
              ) : (
                <Tooltip title={label} arrow placement="bottom" key={index}>
                  <IconButton
                    component={Link}
                    to={path}
                    sx={{
                      border: "3px solid #e3f2fd",
                      borderRadius: "50%",
                      padding: isTablet ? 0.8 : 1,
                      transition: "0.3s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Icon sx={{ color: "#333", fontSize: iconSize }} />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        )}

        {/* Right Avatar/Menu */}
        <Box display="flex" alignItems="center" gap={1} zIndex={2}>
          {isMobile ? (
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: "auto" }}
                aria-label="open drawer"
              >
                <MenuIcon sx={{ fontSize: 30 }} />
              </IconButton>

              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
              >
                <Box sx={{ width: 250 }} role="presentation">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      p: 1,
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setDrawerOpen(false)}
                      aria-label="close drawer"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <List>
                    {navItems.map(({ icon: Icon, path, label }, index) => (
                      <ListItem
                        button
                        key={index}
                        component={Link}
                        to={path}
                        selected={location.pathname === path}
                        onClick={() => setDrawerOpen(false)}
                      >
                        <ListItemIcon>
                          <Icon />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ fontSize: "1rem" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                  <List>
                    <ListItem button onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{ fontSize: "1rem" }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Drawer>
            </>
          ) : (
            <Box>
              <IconButton onClick={handleMenuOpen}>
                <Avatar
                  src={accountImg}
                  alt="User"
                  sx={{ width: 35, height: 40, cursor: "pointer" }}
                />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: { paddingTop: 1, paddingBottom: 1, minWidth: 180 },
                }}
              >
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    backgroundColor: "#d32f2f",
                    color: "white",
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "#b71c1c" },
                  }}
                >
                  <LogoutIcon fontSize="small" /> Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default UserSidebar;
